import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';

interface StepDetail {
  text: string;
  icon: React.ReactNode;
  action: string;
}

interface StepCardProps {
  title: string;
  description: string;
  chipLabel: string;
  icon: React.ReactNode;
  details: StepDetail[];
  onClick: (action: string) => void;
}

const CardContainer = styled(motion(Box))(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(3),
  background: 'rgba(26, 26, 46, 0.6)',
  backdropFilter: 'blur(5px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background: 'linear-gradient(0deg, transparent, transparent 30%, rgba(0, 255, 255, 0.3))',
    transform: 'rotate(-45deg)',
    transition: 'all 0.5s ease',
    opacity: 0,
  },
  '&:hover': {
    background: 'rgba(26, 26, 46, 0.8)',
    transform: 'scale(1.02)',
    boxShadow: '0 0 20px rgba(0, 255, 255, 0.5)',
    border: '1px solid rgba(0, 255, 255, 0.3)',
    '&::before': {
      opacity: 1,
      transform: 'rotate(-45deg) translateY(100%)',
    },
  }
}));

const FeatureList = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  '& > div': {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
    borderRadius: theme.spacing(1),
    transition: 'all 0.3s ease',
    '&:hover': {
      background: 'linear-gradient(90deg, #003366, #00CC99, #8A2BE2, #FF6F00)',
      backgroundSize: '300% 100%',
      animation: 'gradient 6s linear infinite',
      cursor: 'pointer',
    },
    '& svg': {
      marginRight: theme.spacing(1),
      color: '#00FFFF',
    }
  }
}));

const StepCard: React.FC<StepCardProps> = ({
  title,
  description,
  chipLabel,
  icon,
  details,
  onClick
}) => {
  return (
    <CardContainer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ color: '#00FFFF', display: 'flex', alignItems: 'center' }}>
          {icon}
          <Box component="span" sx={{ ml: 1 }}>{title}</Box>
        </Typography>
        <Chip 
          label={chipLabel} 
          size="small"
          sx={{ 
            backgroundColor: 'rgba(138, 43, 226, 0.2)',
            color: '#FFFFFF',
            borderRadius: '4px'
          }}
        />
      </Box>
      
      <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 2 }}>
        {description}
      </Typography>
      
      <FeatureList>
        {details.map((detail, index) => (
          <Box key={index} onClick={() => onClick(detail.action)}>
            {detail.icon}
            <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
              {detail.text}
            </Typography>
          </Box>
        ))}
      </FeatureList>
    </CardContainer>
  );
};

export default StepCard; 